import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import {
  useZonaSocialOptions,
  ZONAS_SOCIALES_ENDPOINT,
} from "./useZonaSocialOptions";

const DEFAULT_LABEL = "Zona social";
const DEFAULT_PLACEHOLDER = "Selecciona una zona";

const ZonaSocialSelectReact = React.forwardRef(
  (
    {
      value = "",
      onChange,
      name = "zona_uuid",
      id,
      label = DEFAULT_LABEL,
      showLabel = true,
      placeholder = DEFAULT_PLACEHOLDER,
      allowAllOption = false,
      allOptionValue = "all",
      allOptionLabel = "Todas las zonas",
      options,
      autoFetch = true,
      endpoint = ZONAS_SOCIALES_ENDPOINT,
      params,
      disabled = false,
      containerClassName,
      labelClassName = "form-label fw-semibold",
      helperText,
      isLoading: externalLoading,
    },
    ref,
  ) => {
    const { zones: fetchedZones, isLoading } = useZonaSocialOptions({
      endpoint,
      params,
      autoFetch: options ? false : autoFetch,
      initialOptions: options,
    });

    const effectiveLoading = externalLoading || isLoading;

    const normalizedOptions = useMemo(() => {
      const base = Array.isArray(fetchedZones) ? fetchedZones : [];
      const map = new Map();

      base.forEach((option) => {
        if (!option) return;
        const uuid = String(option.uuid ?? option.value ?? option.id ?? "");
        const nombre = String(
          option.nombre ?? option.label ?? option.name ?? "",
        );
        if (!uuid || !nombre) return;
        map.set(uuid, { value: uuid, label: nombre });
      });

      if (allowAllOption) {
        map.set(String(allOptionValue), {
          value: String(allOptionValue),
          label: allOptionLabel,
        });
      }

      return Array.from(map.values());
    }, [fetchedZones, allowAllOption, allOptionValue, allOptionLabel]);

    const selectedValue = useMemo(() => {
      if (!value) return null;
      return (
        normalizedOptions.find((opt) => opt.value === String(value)) || null
      );
    }, [normalizedOptions, value]);

    const handleChange = (option) => {
      const newValue = option?.value || "";
      onChange?.(newValue, option);
    };

    const resolvedId = id || name || "zona-social-select";

    return (
      <div className={containerClassName}>
        {showLabel && (
          <label className={labelClassName} htmlFor={resolvedId}>
            {label}
          </label>
        )}

        <Select
          ref={ref}
          id={resolvedId}
          name={name}
          value={selectedValue}
          onChange={handleChange}
          options={normalizedOptions}
          isClearable={!allowAllOption}
          isSearchable
          placeholder={effectiveLoading ? "Cargando..." : placeholder}
          isDisabled={disabled || effectiveLoading}
          isLoading={effectiveLoading}
          noOptionsMessage={() => "No hay zonas"}
        />

        {helperText && <div className="form-text">{helperText}</div>}
      </div>
    );
  },
);

ZonaSocialSelectReact.displayName = "ZonaSocialSelectReact";

export default ZonaSocialSelectReact;
